import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Landmark, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { atoDebtAPI, ATO_DEBT_TYPE_LABELS, ATO_DEBT_STATUS_COLORS } from '../lib/atoDebtAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

const fmtMoney = (n) => `$${(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ATODebt() {
  const navigate = useNavigate();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ client_id: '', debt_type: 'income_tax', original_amount: '', due_date: '', notes: '' });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, sRes, cRes] = await Promise.all([
        atoDebtAPI.list(),
        atoDebtAPI.dashboard(),
        clientsAPI.list({ status: 'active' }),
      ]);
      setDebts(dRes.data);
      setSummary(sRes.data);
      setClients(cRes.data);
    } catch (error) {
      toast.error('Failed to load ATO debts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.original_amount) {
      toast.error('Client and amount are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await atoDebtAPI.create({
        client_id: form.client_id,
        debt_type: form.debt_type,
        original_amount: parseFloat(form.original_amount),
        due_date: form.due_date || null,
        notes: form.notes || null,
      });
      toast.success('Debt recorded');
      setDialogOpen(false);
      navigate(`/ato-debt/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create debt');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded"></div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="ato-debt-title">
            ATO Debt
          </h1>
          <p className="text-muted-foreground mt-1">Track client debts, payment plans and instalment schedules</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="new-debt-button">
              <Plus className="w-4 h-4 mr-2" /> Record Debt
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record ATO Debt</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="debt-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Debt Type</Label>
                  <Select value={form.debt_type} onValueChange={(v) => setForm({ ...form, debt_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ATO_DEBT_TYPE_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" value={form.original_amount} onChange={(e) => setForm({ ...form, original_amount: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Record'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Outstanding</p>
            <p className="text-3xl font-bold text-red-600 mt-1">{fmtMoney(summary.total_outstanding)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Debts Tracked</p>
            <p className="text-3xl font-bold text-foreground mt-1">{summary.debt_count}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">On Payment Plan</p>
            <p className="text-3xl font-bold text-amber-600 mt-1">{summary.on_payment_plan}</p>
          </CardContent></Card>
        </div>
      )}

      {debts.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <Landmark className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>No ATO debts recorded</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Balance</th>
                    <th className="px-4 py-3 font-medium">Due</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {debts.map((d) => (
                    <tr key={d.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/ato-debt/${d.id}`)} data-testid={`debt-row-${d.id}`}>
                      <td className="px-4 py-3 font-medium text-foreground">{d.client_name || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{ATO_DEBT_TYPE_LABELS[d.debt_type] || d.debt_type}</td>
                      <td className="px-4 py-3 text-foreground/90">{fmtMoney(d.balance)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{d.due_date ? formatAUDate(d.due_date) : '—'}</td>
                      <td className="px-4 py-3"><Badge className={`${ATO_DEBT_STATUS_COLORS[d.status]} text-xs capitalize`}>{d.status?.replace('_', ' ')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
